import { consoleSessionHeld } from 'src/api/consoleSessionHeld'
import { getFirebaseUser } from 'src/firebase'
import { store } from 'src/redux/store'

// ----------------------------------------------------------------------
// The console's Tesla round trip, as the backend holds it.
//
// The trip used to be kept in the tab that started it, so a return that landed
// in any other tab, or in a tab older than the last deploy, could not be
// finished. The backend now keeps it, bound to this browser by an HttpOnly
// cookie it sets on `start`, and `finish` is the only way a return is used.
//
// Both calls go to the console's own origin, never to `VITE_BACKOFFICE_URL`:
// nginx proxies `/backoffice/` here to the same backend, and a cookie set by
// the API's host would be third-party to this page in production, where the
// two are different sites. The browser would drop it, and every trip would be
// unknown on its return.
// ----------------------------------------------------------------------

export type TeslaTripPurpose = 'sign_in' | 'reconnect' | 'connect'

export type TeslaTripStart =
  | {
      purpose: 'sign_in'
      invitationToken?: string | null
      /** Started again by the callback, for a return it could not finish. */
      restart?: boolean
      /**
       * After "I already have one": Tesla is asked for the account again
       * rather than answering from its session with the one that came back.
       */
      switchAccount?: boolean
    }
  | { purpose: 'reconnect'; teslaCredentialId: number }
  /** Connects an account to the fleet this session acts for; signs nobody in. */
  | { purpose: 'connect' }

export type TeslaTripOutcome =
  | {
      purpose: 'sign_in'
      token: string
      invitation: 'accepted' | 'unavailable' | null
    }
  /**
   * The Tesla account is not linked yet and the account it would link to has
   * an authenticator app: its code is owed, answered with this ticket.
   */
  | { purpose: 'sign_in'; secondFactorTicket: string }
  /**
   * No console account uses the Tesla account and none was made: the person
   * is asked first (`newAccountConfirm`). The address is the one Tesla proved.
   */
  | { purpose: 'sign_in'; newAccountTicket: string; email: string }
  | { purpose: 'reconnect'; teslaCredentialId: number; email: string }
  | { purpose: 'connect'; teslaCredentialId: number; email: string }

/**
 * Whether a finished Tesla sign-in owes the account's authenticator code
 * before its Tesla account is linked, rather than bringing a session.
 */
export const teslaTripOwesSecondFactor = (
  outcome: TeslaTripOutcome,
): outcome is { purpose: 'sign_in'; secondFactorTicket: string } =>
  'secondFactorTicket' in outcome &&
  typeof outcome.secondFactorTicket === 'string'

/**
 * Whether a finished Tesla sign-in found no console account and is to ask
 * before one is made, rather than bringing a session.
 */
export const teslaTripAsksNewAccount = (
  outcome: TeslaTripOutcome,
): outcome is { purpose: 'sign_in'; newAccountTicket: string; email: string } =>
  'newAccountTicket' in outcome && typeof outcome.newAccountTicket === 'string'

/** A refusal from the backend, with what it said and what the trip was for. */
export class TeslaTripError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Known once the backend found the trip, whatever went wrong after. */
    readonly purpose: TeslaTripPurpose | null,
    /** On an unknown return: this browser already has a restart under way. */
    readonly restartPending: boolean,
  ) {
    super(message)
    this.name = 'TeslaTripError'
  }
}

const FALLBACK_MESSAGE = 'Tesla sign in failed. Try again.'

interface ErrorBody {
  code?: unknown
  message?: unknown
  purpose?: unknown
  restartPending?: unknown
}

/**
 * Headers naming the console session, only when there is one the backend
 * accepts: a Firebase session without the console claim is refused with 401
 * before the handler runs, and a sign-in needs no session at all.
 */
export const sessionHeaders = async (): Promise<Record<string, string>> => {
  // Asked first, and the user read after: on the callback's fresh page load
  // Firebase has no user until it has read its session back, which is what
  // `consoleSessionHeld` waits for. Read earlier, a signed-in person's repair
  // went without its session and was refused.
  if ((await consoleSessionHeld()) !== true) {
    return {}
  }

  const user = getFirebaseUser()
  if (!user) {
    return {}
  }

  const providerId = store.getState().providers?.providerId

  return {
    authorization: `Bearer ${await user.getIdToken()}`,
    ...(providerId ? { 'x-provider-id': String(providerId) } : {}),
  }
}

const post = async <T>(
  path: 'start' | 'finish',
  body: object,
  withSession: boolean,
): Promise<T> => {
  const response = await fetch(
    `${window.location.origin}/backoffice/auth/tesla/${path}`,
    {
      method: 'POST',
      // The default, stated: the cookie is this origin's and goes only here.
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(withSession ? await sessionHeaders() : {}),
      },
      body: JSON.stringify(body),
    },
  )

  const json = (await response.json().catch(() => ({}))) as T & ErrorBody

  if (!response.ok) {
    throw new TeslaTripError(
      response.status,
      typeof json.code === 'string' ? json.code : 'UNKNOWN',
      typeof json.message === 'string' ? json.message : FALLBACK_MESSAGE,
      json.purpose === 'sign_in' ||
      json.purpose === 'reconnect' ||
      json.purpose === 'connect'
        ? json.purpose
        : null,
      json.restartPending === true,
    )
  }

  return json
}

/** Begins a trip and answers with where to send the browser. */
export const teslaTripStart = async (trip: TeslaTripStart): Promise<string> => {
  // A sign-in needs no session; a repair and a connect act for the fleet
  // this session is on.
  const { url } = await post<{ url: string }>(
    'start',
    trip,
    trip.purpose !== 'sign_in',
  )
  return url
}

/** Finishes the trip Tesla's return belongs to, in this browser. */
export const teslaTripFinish = (
  code: string,
  state: string,
): Promise<TeslaTripOutcome> =>
  post<TeslaTripOutcome>('finish', { code, state }, true)

/** Said when the backend could not be reached to begin a trip. */
const START_UNREACHABLE: Record<TeslaTripPurpose, string> = {
  sign_in:
    'Tesla sign in could not start. Check your connection and try again.',
  connect:
    'Connecting a Tesla account could not start. Check your connection and try again.',
  reconnect: 'The repair could not start. Check your connection and try again.',
}

/** What to tell somebody whose trip could not begin, and for what. */
export const teslaTripStartFailure = (
  error: unknown,
  purpose: TeslaTripPurpose = 'sign_in',
): string =>
  error instanceof TeslaTripError ? error.message : START_UNREACHABLE[purpose]
